import { AlertTriangle, ChevronDown, ChevronUp, Copy, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/use-auth";
import { feedbackApi } from "@/lib/auth-api";
import { cn } from "@/lib/utils";

export interface ErrorPageProps {
	error?: Error | unknown;
	reset?: () => void;
	title?: string;
	errorCode?: string;
	showStack?: boolean;
}

function extractErrorMessage(error: unknown): string {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	try {
		return JSON.stringify(error);
	} catch {
		return "Nieznany błąd";
	}
}

function extractStack(error: unknown): string | undefined {
	if (error instanceof Error) return error.stack;
	return undefined;
}

function buildReport(params: {
	timestamp: string;
	url: string;
	errorCode: string | undefined;
	message: string;
	stack: string | undefined;
}): string {
	const lines: string[] = [
		"=== RAPORT BŁĘDU FARMASTER ===",
		`Timestamp:  ${params.timestamp}`,
		`URL:        ${params.url}`,
	];
	if (params.errorCode) lines.push(`Kod błędu:  ${params.errorCode}`);
	lines.push(`Wiadomość:  ${params.message}`);
	if (params.stack) {
		lines.push("");
		lines.push("--- Stack trace ---");
		lines.push(params.stack);
	}
	lines.push("");
	lines.push(`User-Agent: ${navigator.userAgent}`);
	return lines.join("\n");
}

function buildJson(params: {
	timestamp: string;
	url: string;
	errorCode: string | undefined;
	message: string;
	stack: string | undefined;
}): string {
	return JSON.stringify(
		{
			timestamp: params.timestamp,
			url: params.url,
			errorCode: params.errorCode ?? null,
			message: params.message,
			stack: params.stack ?? null,
			userAgent: navigator.userAgent,
		},
		null,
		2,
	);
}

type CopiedState = "none" | "report" | "json";

async function copyWithFallback(text: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		try {
			const textarea = document.createElement("textarea");
			textarea.value = text;
			textarea.setAttribute("readonly", "");
			textarea.style.position = "fixed";
			textarea.style.left = "-9999px";
			textarea.style.opacity = "0";
			document.body.appendChild(textarea);
			textarea.select();
			textarea.setSelectionRange(0, textarea.value.length);
			const copied = document.execCommand("copy");
			document.body.removeChild(textarea);
			return copied;
		} catch {
			return false;
		}
	}
}

export function ErrorPage({
	error,
	reset,
	title = "Wystąpił błąd aplikacji",
	errorCode,
	showStack = import.meta.env.DEV,
}: ErrorPageProps) {
	const { user } = useAuth();
	const [stackOpen, setStackOpen] = useState(false);
	const [copied, setCopied] = useState<CopiedState>("none");
	const [copyFailed, setCopyFailed] = useState(false);
	const [details, setDetails] = useState("");
	const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
	const [issueStatus, setIssueStatus] = useState<"idle" | "success" | "error">("idle");
	const [issueError, setIssueError] = useState<string | null>(null);
	const [timestamp] = useState(() => new Date().toISOString());
	const url = window.location.href;
	const message = extractErrorMessage(error);
	const stack = extractStack(error);

	const sharedParams = { timestamp, url, errorCode, message, stack };

	async function copyText(text: string, kind: CopiedState) {
		const success = await copyWithFallback(text);
		if (success) {
			setCopyFailed(false);
			setCopied(kind);
			setTimeout(() => setCopied("none"), 2000);
			return;
		}
		setCopyFailed(true);
	}

	async function submitErrorIssue() {
		setIsSubmittingIssue(true);
		setIssueError(null);
		setIssueStatus("idle");

		try {
			const response = await feedbackApi.createIssue({
				issueType: "bug",
				title: `Blad aplikacji: ${message.slice(0, 90)}`,
				description: [
					"Blad zgloszony z Error Page.",
					"",
					"Kroki / kontekst od uzytkownika sa w sekcji 'Dodatkowe szczegoly'.",
				].join("\n"),
				additionalDetails: details.trim() ? details.trim() : undefined,
				context: {
					currentPath: window.location.pathname,
					userAgent: navigator.userAgent,
					screenResolution: `${window.screen.width}x${window.screen.height}`,
					timestamp,
					userName: user?.name,
					userEmail: user?.email,
					userRoles: user?.roles?.join(", "),
					errorCode,
					errorMessage: message,
					errorStack: stack,
				},
			});

			setIssueStatus("success");
			window.open(response.issueUrl, "_blank", "noopener,noreferrer");
		} catch (submitError) {
			setIssueStatus("error");
			setIssueError(submitError instanceof Error ? submitError.message : "Nieznany blad wysylki.");
		} finally {
			setIsSubmittingIssue(false);
		}
	}

	return (
		<div className="flex min-h-screen items-start justify-center bg-background px-4 pt-24">
			<div className="w-full max-w-xl">
				<div className="mb-6 flex items-start gap-3">
					<span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-status-error-border bg-status-error-bg">
						<AlertTriangle className="size-4 text-status-error-text" />
					</span>
					<div>
						<h1 className="text-base font-semibold text-foreground">{title}</h1>
						<p className="mt-0.5 text-sm text-muted-foreground">
							Skopiuj raport i przekaż go do wsparcia technicznego.
						</p>
					</div>
				</div>

				<div className="rounded-lg border border-border bg-card p-4 text-xs">
					<dl className="space-y-2">
						<MetaRow label="Timestamp" value={timestamp} mono />
						<MetaRow label="URL" value={url} mono />
						{errorCode && <MetaRow label="Kod błędu" value={errorCode} mono />}
						<MetaRow label="Wiadomość" value={message} />
					</dl>
				</div>

				{(showStack || stack) && stack && (
					<div className="mt-3 rounded-lg border border-border bg-card">
						<button
							type="button"
							onClick={() => setStackOpen((v) => !v)}
							aria-expanded={stackOpen}
							className="flex w-full items-center justify-between px-4 py-3 text-xs font-medium text-muted-foreground hover:text-foreground"
						>
							<span>Szczegóły techniczne (stack trace)</span>
							{stackOpen ? (
								<ChevronUp className="size-4 shrink-0" />
							) : (
								<ChevronDown className="size-4 shrink-0" />
							)}
						</button>
						{stackOpen && (
							<div className="border-t border-border px-4 pb-4 pt-3">
								<pre className="max-h-64 overflow-auto font-mono text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap break-all">
									{stack}
								</pre>
							</div>
						)}
					</div>
				)}

				<div className="mt-4 flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => copyText(buildReport(sharedParams), "report")}
						className={cn(
							copied === "report" &&
								"border-status-success-border bg-status-success-bg text-status-success-text",
						)}
					>
						<Copy className="size-3.5" />
						{copied === "report" ? "Skopiowano!" : "Kopiuj raport"}
					</Button>

					{copyFailed && (
						<p className="w-full text-xs text-status-error-text">
							Nie udało się skopiować automatycznie. Użyj zaznaczenia ręcznego (Ctrl/Cmd+C).
						</p>
					)}

					<Button
						variant="outline"
						size="sm"
						onClick={() => copyText(buildJson(sharedParams), "json")}
						className={cn(
							copied === "json" &&
								"border-status-success-border bg-status-success-bg text-status-success-text",
						)}
					>
						<Copy className="size-3.5" />
						{copied === "json" ? "Skopiowano!" : "Kopiuj JSON"}
					</Button>

					<div className="w-full rounded-lg border border-border bg-card p-3">
						<label
							htmlFor="error-extra-details"
							className="mb-1.5 block text-xs font-medium text-foreground"
						>
							Dodatkowe szczegoly (co robiles w momencie bledu)
						</label>
						<textarea
							id="error-extra-details"
							value={details}
							onChange={(event) => setDetails(event.target.value)}
							rows={4}
							maxLength={2000}
							placeholder="Np. Wchodzilem do raportu i kliknalem eksport..."
							className={cn(
								"w-full rounded-md border border-input bg-background px-3 py-2 text-xs",
								"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
							)}
						/>
						<div className="mt-2 flex items-center justify-between">
							<p className="text-xs text-muted-foreground">{details.length}/2000</p>
							<Button size="sm" onClick={submitErrorIssue} disabled={isSubmittingIssue}>
								{isSubmittingIssue ? "Wysylanie..." : "Zglos ten blad"}
							</Button>
						</div>
						{issueStatus === "success" ? (
							<p className="mt-2 text-xs text-status-success-text">
								Zgloszenie zostalo utworzone. Otworzylismy issue w nowej karcie.
							</p>
						) : null}
						{issueStatus === "error" ? (
							<p className="mt-2 text-xs text-status-error-text">
								Nie udalo sie utworzyc zgloszenia: {issueError}
							</p>
						) : null}
					</div>

					<div className="flex-1" />

					<Button variant="ghost" size="sm" onClick={() => window.history.back()}>
						Wróć
					</Button>

					{reset ? (
						<Button size="sm" onClick={reset}>
							<RefreshCw className="size-3.5" />
							Spróbuj ponownie
						</Button>
					) : (
						<Button size="sm" onClick={() => window.location.reload()}>
							<RefreshCw className="size-3.5" />
							Odśwież stronę
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}

function MetaRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
	return (
		<div className="flex gap-3">
			<dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
			<dd className={cn("min-w-0 break-all text-foreground", mono && "font-mono")}>{value}</dd>
		</div>
	);
}
